import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Send, CheckCircle2 } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { Field, inputClass } from '@/components/calcUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  useSeo({
    title: 'Contact',
    description: 'Get in touch with the CalculateEasy team — bug reports, tool requests, partnership enquiries.',
    path: '/contact',
  });

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', website: '' });
  const [status, setStatus] = useState({ state: 'idle', error: null });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || form.message.trim().length < 5) {
      setStatus({ state: 'error', error: 'Please fill out every field. Message must be at least 5 characters.' });
      return;
    }
    setStatus({ state: 'sending', error: null });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: 'success', error: null });
      setForm({ name: '', email: '', subject: '', message: '', website: '' });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = 'Something went wrong. Please try again in a moment.';
      if (status === 429) msg = typeof detail === 'string' ? detail : 'Too many submissions. Please try again later.';
      else if (typeof detail === 'string') msg = detail;
      setStatus({ state: 'error', error: msg });
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <header className="mb-8">
        <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
          <Mail className="w-6 h-6" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">Get in touch</h1>
        <p className="text-slate-600 mt-2">Feedback, bug reports, or a tool you'd love to see — we read every message.</p>
      </header>

      {status.state === 'success' ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 flex gap-3" data-testid="contact-success">
          <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-emerald-900">Thanks — your message is in.</div>
            <p className="text-emerald-800 text-sm mt-1">We'll be in touch as soon as we can.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border border-slate-100 rounded-2xl p-6 sm:p-8 space-y-5" noValidate>
          {/* Honeypot: hidden from users, visible to bots. Real submissions leave it empty. */}
          <div aria-hidden="true" className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden">
            <label htmlFor="contact-website">Website (leave blank)</label>
            <input
              id="contact-website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={update('website')}
              data-testid="contact-honeypot"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Your name" htmlFor="contact-name">
              <input id="contact-name" data-testid="contact-name" value={form.name} onChange={update('name')} className={inputClass} required />
            </Field>
            <Field label="Email address" htmlFor="contact-email">
              <input id="contact-email" data-testid="contact-email" type="email" value={form.email} onChange={update('email')} className={inputClass} required />
            </Field>
          </div>
          <Field label="Subject" htmlFor="contact-subject">
            <input id="contact-subject" data-testid="contact-subject" value={form.subject} onChange={update('subject')} className={inputClass} required />
          </Field>
          <Field label="Message" htmlFor="contact-message">
            <textarea id="contact-message" data-testid="contact-message" value={form.message} onChange={update('message')} rows={6} className={inputClass + ' h-auto py-3'} required />
          </Field>
          {status.error && (
            <div className="text-sm text-rose-600" data-testid="contact-error" role="alert">{status.error}</div>
          )}
          <button
            type="submit"
            disabled={status.state === 'sending'}
            data-testid="contact-submit"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {status.state === 'sending' ? 'Sending…' : 'Send message'}
          </button>
        </form>
      )}
    </div>
  );
}
